/**
 * Card de curso da vitrine (retrato ~2:3).
 *
 * Apresentacional: não decide acesso, só traduz o `ResolvedCourse` para as props
 * do PlatformCard. O que acontece no clique é responsabilidade do PlatformHome.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResolvedCourse } from './mergeCourses';
import PlatformCard from './PlatformCard';

interface CourseCardProps {
  course: ResolvedCourse;
  onClick: (course: ResolvedCourse) => void;
  /** Mostra spinner no card durante a troca de curso. */
  isBusy?: boolean;
  /** Mensagem de erro/aviso exibida sob o card (ex.: sem acesso, bloqueado). */
  notice?: string | null;
  /**
   * Sinaliza falta de matrícula com um cadeado. Só faz sentido com sessão: para
   * o visitante anônimo nenhum curso está "liberado", e marcar todos com
   * cadeado viraria ruído — a vitrine é justamente o convite a entrar.
   */
  showAccessState?: boolean;
}

const CourseCard: React.FC<CourseCardProps> = memo(
  ({ course, onClick, isBusy = false, notice, showAccessState = false }) => {
    const { t } = useTranslation();

    const isSoon = course.status === 'soon';
    const isLocked = showAccessState && !isSoon && !course.enrolled && !course.externalUrl;

    const badgeLabel = course.badge === 'new' ? t('platform.badge.new') : null;
    // "Em breve" no card vale sempre que não há conteúdo, mesmo sem badge
    // declarado no catálogo — o estado real vence a configuração.
    const soonLabel = isSoon ? t('platform.badge.soon') : null;

    return (
      <div className="flex flex-col gap-2">
        <PlatformCard
          variant="vertical"
          title={course.title}
          subtitle={course.subtitle}
          image={course.cover}
          gradient={course.gradient}
          tag={soonLabel ?? badgeLabel}
          tagTone={soonLabel ? 'muted' : 'accent'}
          locked={isLocked}
          lockedTitle={t('platform.noAccess')}
          busy={isBusy}
          dimmed={isSoon}
          highlighted={course.isCurrent}
          onClick={() => onClick(course)}
          testId={`course-card-${course.slug}`}
        />

        {notice && (
          <p className="px-1 text-xs font-medium text-mv-accent-hi" role="status">
            {notice}
          </p>
        )}
      </div>
    );
  },
);

CourseCard.displayName = 'CourseCard';

export default CourseCard;
